// pages/SearchResults.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Select, Spin, Pagination, Empty } from "antd";

const FallbackCardImg =
  "https://snasso.quintbrothers.com/wp-content/uploads/2025/09/defulat-image-scaled.webp";

const useQuery = () => new URLSearchParams(useLocation().search);

const SearchResults = () => {
  const navigate = useNavigate();
  const query = useQuery();

  // Read query params
  const [keyword, setKeyword] = useState(query.get("keyword") || "");
  const [locationText, setLocationText] = useState(query.get("location") || "");
  const [categoryId, setCategoryId] = useState(query.get("category") || "");
  const [page, setPage] = useState(Number(query.get("page")) || 1);
  const [perPage, setPerPage] = useState(12);

  // Categories for the filter bar
  const [categories, setCategories] = useState([]);
  const [catsLoading, setCatsLoading] = useState(true);

  // Results
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  // Fetch categories once
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://biz.dropyourbusiness.com/api/categories");
        const data = await r.json();
        setCategories(Array.isArray(data) ? data : []);
      } catch {
        setCategories([]);
      } finally {
        setCatsLoading(false);
      }
    })();
  }, []);

  const catOptions = useMemo(
    () =>
      categories.map((c) => ({ label: c.Category_Name, value: String(c.Category_Id) })),
    [categories]
  );

  // Build API URL from filters
  const buildApiUrl = (pageNum = page, per = perPage) => {
    const url = new URL("https://biz.dropyourbusiness.com/api/business/search");
    if (categoryId) url.searchParams.set("category", categoryId);
    url.searchParams.set("per_page", String(per));
    url.searchParams.set("page", String(pageNum));
    url.searchParams.set("status", "A");
    url.searchParams.set("is_active", "1");
    // If the API supports these: send both common keys (it will ignore unknowns safely)
    if (keyword.trim()) {
      url.searchParams.set("name", keyword.trim());
      url.searchParams.set("q", keyword.trim());
    }
    if (locationText.trim()) {
      url.searchParams.set("city_name", locationText.trim());
      // url.searchParams.set("state_name", locationText.trim());
    }
    return url.toString();
  };

  const fetchResults = async (pageNum = page, per = perPage) => {
    setLoading(true);
    try {
      const r = await fetch(buildApiUrl(pageNum, per));
      const data = await r.json();
      const list = data?.data || data?.results || data?.items || data?.businesses || [];
      setItems(Array.isArray(list) ? list : []);
      setTotal(
        Number(data?.total) ||
          Number(data?.meta?.total) ||
          Number(data?.count) ||
          (Array.isArray(list) ? list.length : 0)
      );
    } catch (e) {
      console.error("Search error", e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch whenever filters/page change
  useEffect(() => {
    fetchResults(page, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, keyword, locationText, page, perPage]);

  // Keep URL in sync (so back/forward works)
  const syncUrl = (p = page) => {
    const params = new URLSearchParams();
    if (keyword.trim()) params.set("keyword", keyword.trim());
    if (locationText.trim()) params.set("location", locationText.trim());
    if (categoryId) params.set("category", String(categoryId));
    params.set("page", String(p));
    navigate(`/search?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    syncUrl(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, locationText, categoryId, page]);

  const openBusiness = (b) => {
    const slug =
      b?.live_url?.replace?.(/^https?:\/\/[^/]+\//i, "") ||
      b?.live_url ||
      b?.slug ||
      b?.id ||
      b?.B_Id;

    if (!slug) return;

    navigate(`/business/${slug}`, { state: { business: normalizeBiz(b) } });
  };

  const normalizeBiz = (b) => ({
    business_name: b?.business_name || b?.B_Name || b?.name || "Untitled",
    images: b?.images || b?.B_Images || [],
    logo: b?.logo || b?.B_Logo || "",
    description: b?.description || b?.B_Description || "",
    live_url: b?.live_url || b?.B_live_url || "",
    owner_name: b?.owner_name || b?.B_Owner_Name || "",
    category: b?.category || { name: b?.Category_Name },
    sub_category: b?.sub_category,
    address:
      b?.address || {
        street_address_1: b?.address_line_1 || "",
        street_address_2: b?.address_line_2 || "",
        city: b?.city ? { name: b.city } : undefined,
        state: b?.state ? { name: b.state } : undefined,
        country: b?.country ? { name: b.country } : undefined,
        pincode: b?.pincode || b?.zip || "",
        email: b?.email || "",
        contact_1: b?.phone || b?.contact || "",
        url: b?.website || "",
      },
    social_accounts: b?.social_accounts || {},
    business_time: b?.business_time || {},
  });

  const thumb = (b) => {
    if (Array.isArray(b?.images) && b.images[0]) return b.images[0];
    if (Array.isArray(b?.B_Images) && b.B_Images[0]) return b.B_Images[0];
    if (b?.hero) return b.hero;
    if (b?.logo) return b.logo;
    return FallbackCardImg;
  };

  return (
    <div className="search-results-page">
      {/* Filters row */}
      <div className="sr-filters">
        <input
          className="sr-input"
          placeholder="Keywords…"
          value={keyword}
          onChange={(e) => {
            setPage(1);
            setKeyword(e.target.value);
          }}
        />
        <input
          className="sr-input"
          placeholder="Location…"
          value={locationText}
          onChange={(e) => {
            setPage(1);
            setLocationText(e.target.value);
          }}
        />
        <Select
          showSearch
          allowClear
          placeholder="Category"
          loading={catsLoading}
          options={catOptions}
          value={categoryId || undefined}
          optionFilterProp="label"
          style={{ minWidth: 220 , height:40 }}
          onChange={(val) => {
            setPage(1);
            setCategoryId(val || "");
          }}
        />
      </div>

      {/* Results */}
      <Spin spinning={loading} tip="Loading results…">
        {!loading && items.length === 0 ? (
          <Empty description="No businesses found" />
        ) : (
          <>
            <div className="sr-grid">
              {items.map((b, i) => (
                <article
                  key={b.id || b.B_Id || b.live_url || i}
                  className="sr-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => openBusiness(b)}
                  onKeyDown={(e) => e.key === "Enter" && openBusiness(b)}
                >
                  <div className="sr-thumb">
                    <img
                      src={thumb(b)}
                      alt={b?.business_name || b?.B_Name || "Business"}
                      onError={(e) => (e.currentTarget.src = FallbackCardImg)}
                    />
                  </div>
                  <div className="sr-body">
                    <h3 className="sr-title">{b?.business_name || b?.B_Name || "Untitled"}</h3>
                    {(b?.category?.name || b?.Category_Name) && (
                      <p className="sr-meta">{b?.category?.name || b?.Category_Name}</p>
                    )}
                    {(b?.city || b?.address?.city?.name) && (
                      <p className="sr-loc">
                        {b?.city || b?.address?.city?.name}
                      </p>
                    )}
                    <button className="sr-btn">View Details</button>
                  </div>
                </article>
              ))}
            </div>

            <div className="sr-pagination">
              <Pagination
                current={page}
                total={total}
                pageSize={perPage}
                showSizeChanger
                onChange={(p, ps) => {
                  setPage(p);
                  if (ps !== perPage) setPerPage(ps);
                }}
              />
            </div>
          </>
        )}
      </Spin>

      {/* quick styles; move to CSS if you like */}
      <style>{`
        .search-results-page { padding: 20px 16px 36px; max-width: 1240px; margin: 0 auto; }
        .sr-filters {
          display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px;
        }
        .sr-input {
          height: 40px; padding: 0 12px; border: 1px solid #e5e7eb; border-radius: 8px;
          min-width: 220px; outline: none;
        }
        .sr-input:focus { border-color: #c7ccd1; }
        .sr-grid {
          display: grid; gap: 16px; grid-template-columns: repeat(4, 1fr);
        }
        @media (max-width: 1200px) { .sr-grid { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 900px) { .sr-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 560px) { .sr-grid { grid-template-columns: 1fr; } }
        .sr-card {
          background: #fff; border: 1px solid #eef2f6; border-radius: 12px; overflow: hidden;
          cursor: pointer; display: flex; flex-direction: column; transition: .2s ease;
        }
        .sr-card:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(16,24,40,.08); border-color: #e6ebf2; }
        .sr-thumb { aspect-ratio: 16/9; background: #f5f7f8; }
        .sr-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .sr-body { padding: 12px 14px 16px; display: flex; flex-direction: column; gap: 6px; }
        .sr-title { margin: 0; font-size: 16px; font-weight: 700; color: #111827; }
        .sr-meta, .sr-loc { margin: 0; font-size: 13px; color: #6b7280; }
        .sr-btn { margin-top: auto; align-self: flex-start; border: 0; background: #ff4d4f; color: #fff; border-radius: 8px; padding: 8px 12px; font-weight: 600; }
        .sr-pagination { margin-top: 18px; display: flex; justify-content: center; }
      `}</style>
    </div>
  );
};

export default SearchResults;
